import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { DarkSection, Eyebrow } from "@/components/brand/Bits";
import { OPERATING_MODEL as OM } from "@/data/siteContent";

export function OperatingModel() {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(0);
  const step = OM.steps[active];

  return (
    <DarkSection testId="operating-model">
      <div className="max-w-3xl">
        <Eyebrow dark>{OM.eyebrow}</Eyebrow>
        <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          {OM.title}
        </h2>
        <p className="mt-5 text-base leading-relaxed text-white/80">{OM.body}</p>
      </div>

      <div className="mt-11 hidden lg:block" data-testid="operating-steps-desktop">
        <div
          role="tablist"
          aria-label="Operating model steps"
          className="relative flex items-start justify-between gap-2"
        >
          <span
            aria-hidden
            className="absolute left-[8%] right-[8%] top-5 h-px bg-white/20"
          />
          {OM.steps.map((s, i) => {
            const on = i === active;
            return (
              <button
                key={s.key}
                role="tab"
                id={`om-tab-${s.key}`}
                aria-selected={on}
                aria-controls="om-panel"
                tabIndex={on ? 0 : -1}
                onClick={() => setActive(i)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight") setActive((i + 1) % OM.steps.length);
                  if (e.key === "ArrowLeft") setActive((i - 1 + OM.steps.length) % OM.steps.length);
                }}
                className="relative z-[1] flex w-1/5 flex-col items-center gap-3 px-2 text-center"
                data-testid={`operating-step-${s.key}`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-extrabold transition-colors duration-200 ${
                    on
                      ? "border-[#F2811D] bg-[#F2811D] text-white"
                      : "border-white/30 bg-[#141210] text-white/70 hover:border-white/70 hover:text-white"
                  }`}
                >
                  {i + 1}
                </span>
                <span
                  className={`font-heading text-base font-extrabold transition-colors duration-200 ${
                    on ? "text-white" : "text-white/65"
                  }`}
                >
                  {s.name}
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/40">
                  {s.short}
                </span>
              </button>
            );
          })}
        </div>

        <div
          id="om-panel"
          role="tabpanel"
          aria-labelledby={`om-tab-${step.key}`}
          className="card-lift mt-9 rounded-2xl border border-white/15 bg-white/[0.04] p-7 backdrop-blur-sm"
          data-testid="operating-panel"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFB25C]">
            Step {active + 1} — {step.short}
          </p>
          <h3 className="mt-3 font-heading text-2xl font-extrabold text-white">{step.name}</h3>
          <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-white/80">{step.body}</p>
        </div>
      </div>

      <div className="mt-9 space-y-3 lg:hidden" data-testid="operating-steps-mobile">
        {OM.steps.map((s, i) => {
          const on = i === open;
          return (
            <div
              key={s.key}
              className="overflow-hidden rounded-2xl border border-white/15 bg-white/[0.04]"
            >
              <button
                onClick={() => {
                  setOpen(on ? -1 : i);
                  if (!on) setActive(i);
                }}
                aria-expanded={on}
                aria-controls={`om-acc-${s.key}`}
                className="flex w-full items-center gap-3 p-4 text-left"
                data-testid={`operating-accordion-${s.key}`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-extrabold ${
                    on ? "border-[#F2811D] bg-[#F2811D] text-white" : "border-white/30 text-white/70"
                  }`}
                >
                  {i + 1}
                </span>
                <span className="flex-1">
                  <span className="block font-heading text-base font-extrabold text-white">
                    {s.name}
                  </span>
                  <span className="block text-[11px] font-bold uppercase tracking-[0.16em] text-white/40">
                    {s.short}
                  </span>
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-white/60 transition-transform duration-200 ${
                    on ? "rotate-180" : ""
                  }`}
                />
              </button>
              {on && (
                <p
                  id={`om-acc-${s.key}`}
                  className="px-4 pb-4 text-[15px] leading-relaxed text-white/80"
                  data-testid={`operating-body-${s.key}`}
                >
                  {s.body}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-8 text-sm text-white/60" data-testid="operating-loop-note">
        {OM.loopNote}
      </p>
    </DarkSection>
  );
}
